// src/features/invoice/components/InvoiceItemForm.jsx
import React from "react";
import {
  Grid,
  TextField,
  IconButton,
  Box,
  Typography,
  Paper,
  InputAdornment,
} from "@mui/material";
import { Delete, Add } from "@mui/icons-material";

// ✅ Helper function to format Indian Rupee
const formatINR = (amount) => {
  if (amount === undefined || amount === null) return "₹0.00";
  const num = Number(amount);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const InvoiceItemForm = ({
  items = [],
  onItemChange,
  onAddItem,
  onRemoveItem,
  readOnly = false,
}) => {
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };

    // Recalculate item total
    const quantity = parseFloat(newItems[index].quantity) || 0;
    const unitPrice = parseFloat(newItems[index].unitPrice) || 0;
    newItems[index].total = quantity * unitPrice;

    onItemChange && onItemChange(newItems);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Invoice Items
        </Typography>
        {!readOnly && (
          <IconButton onClick={onAddItem} color="primary" size="small">
            <Add />
          </IconButton>
        )}
      </Box>

      {items.map((item, index) => (
        <Paper
          key={item.id || index}
          sx={{
            p: 2,
            mb: 2,
            bgcolor: index % 2 === 0 ? "#fafafa" : "white",
            border: "1px solid #f0f0f0",
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: 2,
              borderColor: "#667eea",
            },
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Description"
                value={item.description || ""}
                onChange={(e) =>
                  handleItemChange(index, "description", e.target.value)
                }
                size="small"
                disabled={readOnly}
                required
                placeholder="Enter item description..."
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={item.quantity || 1}
                onChange={(e) =>
                  handleItemChange(
                    index,
                    "quantity",
                    parseFloat(e.target.value) || 0,
                  )
                }
                size="small"
                disabled={readOnly}
                required
                inputProps={{ min: 1 }}
                placeholder="0"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Unit Price (₹)"
                type="number"
                value={item.unitPrice || 0}
                onChange={(e) =>
                  handleItemChange(
                    index,
                    "unitPrice",
                    parseFloat(e.target.value) || 0,
                  )
                }
                size="small"
                disabled={readOnly}
                required
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#667eea" }}
                      >
                        ₹
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                placeholder="0.00"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Discount %"
                type="number"
                value={item.discount || 0}
                onChange={(e) =>
                  handleItemChange(
                    index,
                    "discount",
                    parseFloat(e.target.value) || 0,
                  )
                }
                size="small"
                disabled={readOnly}
                inputProps={{ min: 0, max: 100 }}
                placeholder="0"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2" color="text.secondary">
                        %
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4} md={1}>
              <Box sx={{ textAlign: "right" }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="primary"
                  sx={{
                    fontSize: "0.9rem",
                    backgroundColor: "#667eea15",
                    padding: "4px 8px",
                    borderRadius: 1,
                    display: "inline-block",
                  }}
                >
                  {formatINR(item.total || 0)}
                </Typography>
              </Box>
            </Grid>
            {!readOnly && (
              <Grid item xs={2} md={1}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onRemoveItem && onRemoveItem(index)}
                  disabled={items.length === 1}
                  sx={{
                    "&:hover": {
                      backgroundColor: "rgba(255, 68, 68, 0.1)",
                    },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Grid>
            )}
          </Grid>

          {item.notes && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                📝 Note: {item.notes}
              </Typography>
            </Box>
          )}
        </Paper>
      ))}

      {/* Subtotal */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 2,
          p: 2,
          bgcolor: "#f5f5f5",
          borderRadius: 2,
          border: "1px solid #e8e8e8",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="text.secondary"
          >
            Subtotal:
          </Typography>
          <Typography
            variant="h6"
            fontWeight="bold"
            color="primary"
            sx={{
              backgroundColor: "#667eea20",
              padding: "4px 16px",
              borderRadius: 2,
            }}
          >
            {formatINR(calculateSubtotal())}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoiceItemForm;
